import { useEffect, useRef } from 'react';

interface ReasonFieldProps {
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    label: string;
    placeholder: string;
    minLength: number;
    maxLength: number;
    tone?: ReasonFieldTone;
}

type ReasonFieldTone = 'red' | 'amber';

const TONE_FOCUS_CLASSES: Record<ReasonFieldTone, { ring: string; border: string }> = {
    red: { ring: 'focus:ring-red-100', border: 'focus:border-red-400' },
    amber: { ring: 'focus:ring-amber-100', border: 'focus:border-amber-400' }
};

export function ReasonField({
    value,
    onChange,
    disabled,
    label,
    placeholder,
    minLength,
    maxLength,
    tone = 'red'
}: ReasonFieldProps) {
    const trimmed = value.trim();
    const tooShort = trimmed.length > 0 && trimmed.length < minLength;
    const overLimit = trimmed.length > maxLength;
    const focus = TONE_FOCUS_CLASSES[tone];
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Focus on mount and again when re-enabled after a failed submit.
    useEffect(() => {
        if (!disabled) textareaRef.current?.focus();
    }, [disabled]);

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-700">{label}</label>
                <span
                    className={`text-[11px] tabular-nums ${overLimit ? 'text-red-500' : 'text-gray-400'}`}
                >
                    {trimmed.length.toLocaleString()} / {maxLength.toLocaleString()}
                </span>
            </div>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={4}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 resize-none leading-relaxed disabled:opacity-50 ${focus.ring} ${
                    tooShort || overLimit
                        ? 'border-red-300 focus:border-red-400'
                        : `border-gray-300 ${focus.border}`
                }`}
            />
            {tooShort && (
                <p className="text-[11px] text-red-500">Add at least {minLength} characters.</p>
            )}
        </div>
    );
}
